import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { MENU_STYLE, TabsEnum } from "../../../utils/enums";

export enum LoadingActionEnum {
  INCREASE = "increase",
  DECREASE = "decrease",
}
interface LoadingStack {
  action: LoadingActionEnum;
  functionName: string;
  date: Date;
}

export interface InitialState {
  isOpenAddressSelection: boolean;
  animatedGlobalBanner: {
    isOpen: boolean;
    text: string;
    warning: boolean;
    info?: boolean;
  };
  loading: boolean;
  pendingOperations: number;
  loadingStack: LoadingStack[];
  globalSnackBar: {
    isOpen: boolean;
    text: string;
  };
  loyaltyTermsAndConditionsDrawer: {
    isOpen: boolean;
    selectedMilestone: number;
  };
  selectScheduleTimeDrawer: {
    isOpen: boolean;
  };
  selectPickupTimeDrawer: {
    isOpen: boolean;
  };
  currentHomeScreenTab: TabsEnum;
  acceptedTermsAndConditions: boolean;
  isMobile: boolean;
  scrollYOfCategoryScreen: number;
  changingRestaurant: boolean;
  bottomSheetModal: {
    isOpen: boolean;
    source: string | null;
  };
  orderDataLoading: boolean;
  rewardTabClicked?: boolean;
  menuView: MENU_STYLE;
}

const initialState: InitialState = {
  isOpenAddressSelection: false,
  animatedGlobalBanner: {
    isOpen: false,
    text: "",
    warning: false,
  },
  loading: false,
  globalSnackBar: {
    isOpen: false,
    text: "",
  },
  loyaltyTermsAndConditionsDrawer: {
    isOpen: false,
    selectedMilestone: 0,
  },
  selectScheduleTimeDrawer: {
    isOpen: false,
  },
  selectPickupTimeDrawer: {
    isOpen: false,
  },
  currentHomeScreenTab: TabsEnum.Menu,
  acceptedTermsAndConditions: false,
  isMobile: true,
  scrollYOfCategoryScreen: 0,
  changingRestaurant: false,
  pendingOperations: 0,
  loadingStack: [],
  bottomSheetModal: {
    isOpen: false,
    source: null,
  },
  orderDataLoading: false,
  menuView: "list" as MENU_STYLE,
};

const globalComponentsToggle = createSlice({
  name: "globalComponentsToggle",
  initialState,
  reducers: {
    setAddressSelectionDrawerState: (state, action) => {
      state.isOpenAddressSelection = action.payload;
    },
    setAnimatedGlobalBannerState: (
      state,
      action: PayloadAction<{
        isOpen: boolean;
        text: string;
        warning: boolean;
        info?: boolean;
      }>
    ) => {
      state.animatedGlobalBanner = action.payload;
    },
    setGlobalSnackBarState: (
      state,
      action: PayloadAction<{
        isOpen: boolean;
        text: string;
      }>
    ) => {
      state.globalSnackBar = action.payload;
    },
    closeGlobalSnackBar: (state) => {
      state.globalSnackBar = {
        isOpen: false,
        text: "",
      };
    },
    closeAnimatedGlobalBanner: (state) => {
      state.animatedGlobalBanner = {
        isOpen: false,
        text: "",
        warning: false,
      };
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setLoyaltyTermsAndConditionsDrawer: (state, { payload }) => {
      state.loyaltyTermsAndConditionsDrawer = payload;
    },
    setDeliverySelectScheduleTimeDrawer: (state, { payload }) => {
      state.selectScheduleTimeDrawer = payload;
    },
    setScheduledPickupTimeDrawer: (state, { payload }) => {
      state.selectPickupTimeDrawer = payload;
    },
    updateCurrentHomeScreenTab: (state, action) => {
      state.currentHomeScreenTab = action.payload;
    },
    setAcceptedTermsAndConditions: (state, action) => {
      state.acceptedTermsAndConditions = action.payload;
    },
    setIsMobile: (state, action) => {
      state.isMobile = action.payload;
    },
    setChangingRestaurant: (state, action) => {
      state.changingRestaurant = action.payload;
    },
    setScrollYOfCategoryScreen: (state, action) => {
      state.scrollYOfCategoryScreen = action.payload;
    },
    increasePendingOperations: (state) => {
      state.pendingOperations = (state.pendingOperations || 0) + 1;
    },
    decreasePendingOperations: (state) => {
      state.pendingOperations = Math.max(0, (state.pendingOperations || 1) - 1);
    },
    pushToLoadingStack: (state, action: PayloadAction<LoadingStack>) => {
      state.loadingStack.push(action.payload);
    },
    resetLoadingStackAndPendingOperations: (state) => {
      state.loadingStack = [];
      state.pendingOperations = 0;
    },
    setBottomSheetState: (state, action) => {
      if (state.bottomSheetModal.isOpen === action.payload) {
        return;
      }

      state.bottomSheetModal.isOpen = action.payload;
    },
    setBottomSheetSource: (state, action: PayloadAction<string | null>) => {
      state.bottomSheetModal.source = action.payload;
    },
    setOrderDataLoading: (state, action) => {
      state.orderDataLoading = action.payload;
    },
    setRewardTabClicked: (state, action) => {
      state.rewardTabClicked = action.payload;
    },
    setMenuView: (state, action: PayloadAction<MENU_STYLE>) => {
      state.menuView = action.payload;
    },
  },
});

export const {
  setAddressSelectionDrawerState,
  setAnimatedGlobalBannerState,
  closeAnimatedGlobalBanner,
  setLoading,
  setGlobalSnackBarState,
  closeGlobalSnackBar,
  setLoyaltyTermsAndConditionsDrawer,
  setDeliverySelectScheduleTimeDrawer,
  setScheduledPickupTimeDrawer,
  updateCurrentHomeScreenTab,
  setAcceptedTermsAndConditions,
  setIsMobile,
  setScrollYOfCategoryScreen,
  setChangingRestaurant,
  increasePendingOperations,
  decreasePendingOperations,
  pushToLoadingStack,
  resetLoadingStackAndPendingOperations,
  setBottomSheetState,
  setBottomSheetSource,
  setOrderDataLoading,
  setRewardTabClicked,
  setMenuView,
} = globalComponentsToggle.actions;

export default globalComponentsToggle.reducer;
